import { useCallback, useEffect, useState } from "react";
import { useToasts } from "../../contexts/toasts";
import { MotorPattern } from "../../types/Entities";
import { Entities } from "../../api/Entities";
import { Alert, Button, Container, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faPen,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { TailSpin } from "react-loader-spinner";
import { ConfigProvider, Empty, Form, GetProp, Space, Table } from "antd";
import IconButton from "../../common/IconButton";
import { PagedResults } from "../../types/generics";
import MotorPatternForm from "../../Components/Form/MotorPatternForm";
import ListPageHeader from "../../common/ListPageHeader";
import ListPanel from "../../common/ListPanel";

export default function MotorPatternsList() {
  const { showToast } = useToasts();
  const [motorPatterns, setMotorPatterns] = useState<MotorPattern[]>();
  const [error, setError] = useState(false);
  const [totalPatterns, setTotalPatterns] = useState<number>();
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    pageSize: number;
    currentPage: number;
    freeText: string;
  }>({
    pageSize: 20,
    currentPage: 1,
    freeText: "",
  });
  const [addMotorPatternModal, setAddMotorPatternModal] = useState(false);
  const [deleteMotorPatternModal, setDeleteMotorPatternModal] = useState(false);
  const [motorPatternForm] = Form.useForm();
  const [selectedMotorPattern, setSelectedMotorPattern] =
    useState<MotorPattern>();

  const initMotorPatterns = useCallback(() => {
    const urlParams = new URLSearchParams();
    urlParams.append("page", `${filters.currentPage}`);
    urlParams.append("size", `${filters.pageSize}`);
    if (filters.freeText !== "") urlParams.append("freeText", filters.freeText);
    Entities.getMotorPatternsPaginated(urlParams).then(
      ({ data }) => {
        setMotorPatterns((data as PagedResults<MotorPattern>).items);
        setTotalPatterns((data as PagedResults<MotorPattern>).totalItems);
      },
      (err) => {
        showToast(
          "motor-pattern-error",
          "Impossibile ottenere la lista dei Pattern Motori",
          "",
          "danger"
        );
        setMotorPatterns([]);
        setError(true);
      }
    );
  }, [filters, showToast]);

  const reset = useCallback(() => {
    setError(false);
    setMotorPatterns(undefined);
    initMotorPatterns();
  }, [initMotorPatterns]);

  useEffect(() => {
    reset();
  }, [filters, reset]);

  const handlePageChange = useCallback((page: number) => {
    setFilters((previous) => ({
      ...previous,
      currentPage: page,
    }));
  }, []);

  const handlePageSizeChange = useCallback((_current: number, size: number) => {
    setFilters((previous) => ({
      ...previous,
      pageSize: size,
      currentPage: 1,
    }));
  }, []);

  const handleEditMotorPatternClick = useCallback(
    (motorPattern?: MotorPattern) => () => {
      setSelectedMotorPattern(motorPattern);
      setAddMotorPatternModal(true);
    },
    []
  );

  const renderToolbar = useCallback(() => {
    return (
      <ListPageHeader
        title="Pattern Motori"
        count={totalPatterns}
        actions={
          <button
            onClick={handleEditMotorPatternClick()}
            className="button-primary"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">
              Aggiungi Pattern Motorio
            </span>
          </button>
        }
      />
    );
  }, [handleEditMotorPatternClick, totalPatterns]);

  const handleDeleteMotorPatternClick = useCallback(
    (motorPattern: MotorPattern) => () => {
      setSelectedMotorPattern(motorPattern);
      setDeleteMotorPatternModal(true);
    },
    []
  );

  const renderEmpty: GetProp<typeof ConfigProvider, "renderEmpty"> = () => {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Nessun Pattern Motorio"
      />
    );
  };

  const renderMotorPatterns = useCallback(() => {
    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    return (
      <Table
        size="middle"
        bordered
        scroll={{ y: "calc(100vh  280px)" }}
        dataSource={motorPatterns}
        rowKey="id"
        locale={{
          emptyText: renderEmpty(),
        }}
        pagination={{
          pageSize: filters.pageSize,
          current: filters.currentPage,
          onChange: handlePageChange,
          onShowSizeChange: handlePageSizeChange,
          total: totalPatterns,
        }}
      >
        <Table.Column
          key="motorPattern"
          dataIndex="motorPattern"
          title="Nome Pattern"
          className="text-center col-pattern"
        />
        <Table.Column
          key="actions"
          title="Azioni"
          className="text-center col-pattern"
          render={(_value, record: MotorPattern) => (
            <Space size={1}>
              <IconButton
                tooltip="Modifica"
                onClick={handleEditMotorPatternClick(record)}
              >
                <FontAwesomeIcon icon={faPen} />
              </IconButton>
              <IconButton
                tooltip="Elimina"
                className="text-danger"
                onClick={handleDeleteMotorPatternClick(record)}
              >
                <FontAwesomeIcon icon={faTrash} />
              </IconButton>
            </Space>
          )}
        />
      </Table>
    );
  }, [
    motorPatterns,
    error,
    filters,
    handlePageChange,
    handlePageSizeChange,
    totalPatterns,
    handleEditMotorPatternClick,
    handleDeleteMotorPatternClick,
  ]);

  const renderAsyncContent = useCallback(() => {
    return (
      <ListPanel loading={!motorPatterns}>{renderMotorPatterns()}</ListPanel>
    );
  }, [motorPatterns, renderMotorPatterns]);

  const handleAddMotorPatternModalClose = useCallback(() => {
    setAddMotorPatternModal(false);
    motorPatternForm.resetFields();
    setSelectedMotorPattern(undefined);
  }, [motorPatternForm]);

  const handleDeleteMotorPatternModalClose = useCallback(() => {
    setDeleteMotorPatternModal(false);
    setSelectedMotorPattern(undefined);
  }, []);

  const handleEditMotorPatternSubmit = useCallback(
    (values: any) => {
      setLoading(true);

      const body = { ...values };
      const promise = selectedMotorPattern
        ? Entities.updateMotorPattern(selectedMotorPattern.id!, body)
        : Entities.createMotorPattern(body);

      promise.then(
        () => {
          setLoading(false);
          showToast(
            "motor-pattern-success",
            `Pattern Motorio ${
              selectedMotorPattern ? "Modificato" : "Salvato"
            } correttamente`,
            undefined,
            "success"
          );
          handleAddMotorPatternModalClose();
          reset();
        },
        () => {
          setLoading(false);
          showToast(
            "motor-pattern-error",
            `Errore nel${selectedMotorPattern ? "la" : ""} ${
              selectedMotorPattern ? "Modifica" : "Salvataggio"
            } del Pattern Motorio`,
            undefined,
            "danger"
          );
        }
      );
    },
    [selectedMotorPattern, showToast, handleAddMotorPatternModalClose, reset]
  );

  const handleDeleteMotorPattern = useCallback(() => {
    setLoading(true);

    Entities.deleteMotorPattern(selectedMotorPattern!.id!).then(
      () => {
        setLoading(false);
        handleDeleteMotorPatternModalClose();
        reset();
        showToast(
          "delete-motor-pattern-success",
          "Pattern Motorio correttamente eliminato",
          undefined,
          "success"
        );
      },
      () => {
        setLoading(false);
        showToast(
          "delete-motor-pattern-error",
          "Errore nella cancellazione del Pattern Motorio, per favore riprova",
          undefined,
          "danger"
        );
      }
    );
  }, [
    selectedMotorPattern,
    showToast,
    handleDeleteMotorPatternModalClose,
    reset,
  ]);

  return (
    <Container fluid="md" className="mt-5 list-page-container">
      {renderToolbar()}
      {renderAsyncContent()}

      <Modal
        show={addMotorPatternModal}
        onHide={handleAddMotorPatternModalClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMotorPattern ? "Modifica" : "Nuovo"} Pattern Motorio
          </Modal.Title>
        </Modal.Header>
        <MotorPatternForm
          motorPattern={selectedMotorPattern}
          onClose={handleAddMotorPatternModalClose}
          onSubmit={handleEditMotorPatternSubmit}
          loading={loading}
        />
      </Modal>
      <Modal
        show={deleteMotorPatternModal}
        onHide={handleDeleteMotorPatternModalClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedMotorPattern ? "Modifica" : "Nuovo"} Pattern Motorio
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Sei sicuro di voler cancellare il Pattern Motorio{" "}
            <strong>{selectedMotorPattern?.motorPattern}</strong>?
          </p>

          <Alert variant="warning">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            <span className="ms-2">
              Questa azione non può essere annullata.
            </span>
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleDeleteMotorPatternModalClose}
          >
            Chiudi
          </Button>
          <Button variant="danger" size="sm" onClick={handleDeleteMotorPattern}>
            {loading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <span>Cancella</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
